/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable, Inject } from '@angular/core';
import _ from 'lodash';
import emojione from 'emojione';

/**
 * Markdown processing service that wraps the uMarkdown filter logic
 * This service provides markdown-to-HTML conversion with support for:
 * - Quote blocks
 * - Embedly rich media extraction
 * - Image processing with img-crop component
 * - URL linking via ulinky filter
 * - Emoji support
 * - Link validation
 */
@Injectable({
  providedIn: 'root',
})
export class MarkdownService {
  private markdownInstance: any;
  private markdownFunction: any;

  constructor(
    @Inject('EmbedlyService') private EmbedlyService: any,
    @Inject('CompileService') private CompileService: any,
    @Inject('$filter') private $filter: any,
    @Inject('CookieAgreementService') private CookieAgreementService: any,
  ) {
    // Initialize EasyMDE instance for markdown parsing
    this.markdownInstance = this.createEasymdeInstance();
    this.markdownFunction = Object.getPrototypeOf(this.markdownInstance).markdown;
  }

  /**
   * Create a hidden EasyMDE instance for markdown processing
   */
  private createEasymdeInstance(): any {
    const parent = document.createElement('div');
    parent.style.display = 'none';
    const editorElement = document.createElement('textarea');
    parent.appendChild(editorElement);

    // @ts-ignore - EasyMDE is loaded globally
    return new EasyMDE({
      placeholder: ' ',
      element: editorElement,
      toolbar: false,
      status: false,
    });
  }

  /**
   * Convert markdown to HTML using EasyMDE directly, bypassing Embedly enrichment.
   * Used for quiz questions where images are S3-uploaded internal URLs.
   *
   * Why not process(): three reasons.
   * 1. The backend proxies to api.embedly.com/1/oembed, which does not support S3 URLs —
   *    it returns an error, triggering insertLinkOnly() with no key so the image silently
   *    disappears from the DOM entirely.
   * 2. Without targeting cookies, insertLinkOnly() renders a plain text URL instead of an
   *    image, so quiz images would be invisible to users who haven't accepted cookie consent.
   * 3. process() generates a Date.now() key per call; QuizQuestionComponent uses OnPush, so
   *    each change detection cycle replaces the placeholder with a new key, and Embedly's
   *    async jQuery callback can never find the original element.
   *
   * This is consistent with lesson-display, which also renders SimpleMDE images as plain
   * <img> tags with no Embedly or cookie gate.
   *
   * Emoji shortcodes (e.g. `:100:`) are handled here too, rather than by calling the
   * ulinky filter like process() does: ulinky's URL-linking regex would wrap raw image
   * URLs from markdown image syntax (`![alt](url)`) in an <a> tag before EasyMDE parses
   * them, breaking the image rendering above.
   */
  renderMarkdown(input: string): string {
    if (!_.isString(input)) {
      return input;
    }
    input = input.replace(/:[a-z_0-9-]+:/g, (emoji) => emojione.toImage(emoji));
    input = input.replace(
      /(<img.*class=.emojione.*>)(\n)/g,
      (match, imgTag, newLine) => `${imgTag} ${newLine}${newLine}`,
    );
    let result = this.markdownFunction.call(this.markdownInstance, input);
    result = result.replace('<head></head><body>', '').replace('</body>', '');
    return result;
  }

  /**
   * Process markdown content and convert to HTML
   * This method replicates the logic from the uMarkdown filter
   */
  process(input: string): string {
    if (!_.isString(input)) {
      return input;
    }

    const urlRegex = /^(((http|https|ftp|ftps):\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?)|((\/[a-z0-9]+)+(([\-\.]{1}[a-z0-9]+)?)+)|(\#[a-z0-9]+)|(mailto:(([\w+\-].?)+@[a-z\d\-]+(\.[a-z]+)*\.[a-z]+))$/igm;

    // Replace [quote] BBCode with blockquote HTML
    input = input.replace(
      /\[quote[^\]]*\](.*)\[\/quote[^\]]*\]/g,
      (match, quote) => `<blockquote>${quote}</blockquote>`,
    );

    // Replace image URLs with embedly extraction for rich media
    input = input.replace(
      /!\[[^\]]*\]\((https?:\/\/[^) ]*)\)/g,
      (match, url) => {
        const key = Date.now().toString();
        this.EmbedlyService.extract(url, null, key);
        return `<p class="embedly-wrapper" data-embedly-${key}="${_.escape(url)}"></p>\n`;
      },
    );

    // Replace image markdown with img-crop component or plain link
    input = input.replace(
      /!\[([^\]]*)\]\(([^)]*[jpe?g|png|gif][^) ]+)\)/g,
      (match, alt, src) => {
        const id = `img-crop-${src}-${alt}`;

        let template = '<a class="status-attachment_link" target="_blank" rel="noopener noreferrer" ng-href="{{::src}}" >{{::src}}</a>';
        if (this.CookieAgreementService.hasAcceptedTargetingCookies()) {
          template = '<img-crop src="{{::src}}" alt="{{::alt}}"></img-crop>';
        }

        this.CompileService.replace(id, template, { src, alt });
        return `<div data-compile-replace="${_.escape(id)}"></div>\n`;
      },
    );

    // Apply ulinky filter for URL linking
    input = this.$filter('ulinky')(input);

    // Check for emoji followed by newLine and add space in between to force newLine
    input = input.replace(
      /(<img.*class=.emojione.*>)(\n)/g,
      (match, imgTag, newLine) => `${imgTag} ${newLine}${newLine}`,
    );

    // Run through EasyMDE markdown parser
    input = this.markdownFunction.call(this.markdownInstance, input);
    input = input.replace('<head></head><body>', '').replace('</body>', '');

    // Add target="_blank" to links
    const replacement = input.includes('target="_blank"')
      ? '<a rel=no-opener no-referer href='
      : '<a target="_blank" rel=no-opener no-referer href=';
    input = input.replace(/<a\s+href=/gi, replacement);

    // Validate URLs in links
    const urlsWithHref = input.match(/<a(.*?)\s+href="([^"]*)"(.*?)>(.*?)<\s*\/a>/gi);
    if (urlsWithHref !== undefined && urlsWithHref !== null && urlsWithHref.length > 0) {
      urlsWithHref.forEach((url) => {
        url.replace(
          /<a(.*?)\s+href="([^"]*)"(.*?)>(.*?)<\s*\/a>/gi,
          (match, alt, src) => {
            const desc = (/>(.*?)<\s*\/a>/gi).exec(match)?.[1]; // description of 'a' tag
            if (match !== undefined && src !== undefined && !src.match(urlRegex)) {
              input = input.split(match).join(desc || ''); // remove an invalid link
            }
            return match;
          },
        );
      });
    }

    return input;
  }
}
